import { ModalComponent } from '../../components/modal/modal'
import { type Nullable } from '../../types/common'
import { PopupStorage } from './popup-storage'
import { PopupType, type PopupVariant } from './types'
import { ConditionRuntime } from './condition-runtime'
import { POPUP_ANIMATION_DELAY, gradientOpacity } from './utils'
import { POPUP_CLASSNAMES } from './constants'
import { isMobile } from '../../utils/check-media'
import { $el, whenDefined } from '../../utils/utils'
import { BodyElement } from '../../components/body-element/body-element'

const BODY_ELEMENT_SELECTOR = 'body-element'

export class PopupSignup extends ModalComponent {
  storage: PopupStorage
  conditionRuntime?: ConditionRuntime
  variant: PopupVariant
  hasImage: boolean
  hideImgOnMobile: boolean
  isSuccessMessage = false
  signupCheckbox: HTMLInputElement
  submitButton: HTMLButtonElement

  constructor() {
    super()
    this.storage = new PopupStorage(this.dataset.blockId ?? this.id)
    this.variant = this.dataset.variant as PopupVariant
    this.hasImage = this.dataset.hasImage === 'true'
    this.hideImgOnMobile = this.dataset.hideImgMobile === 'true'
  }

  mountComponent(): void {
    if (this.isEditor) {
      this.editor.on('BLOCK_SELECT', this._handleBlockSelect)
      this.editor.on('BLOCK_DESELECT', this._handleBlockDeselect)
      return
    }

    const data = this.storage.loadData()

    // don't show on captcha page
    if (window.location.pathname.includes('challenge')) {
      return
    }

    // handle success message
    if (
      this.dataset.showSuccessMessage === 'true' &&
      window.sessionStorage.getItem('customer-posted-block-id') ===
        this.dataset.blockId
    ) {
      this.show()
      window.sessionStorage.removeItem('customer-posted-block-id')
      this.classList.add(POPUP_CLASSNAMES.success)
      this.isSuccessMessage = true
      return
    }

    if (!data || data.type === PopupType.Signup) {
      if (data?.lastShow) {
        try {
          data.lastShow = new Date(data.lastShow)
        } catch {
          data.lastShow = null
        }
      }

      this.conditionRuntime = new ConditionRuntime(
        this.dataset,
        data?.lastShow ?? null,
        this.show,
      )
      this.conditionRuntime.run()
    }
  }

  unmountComponent(): void {
    this.conditionRuntime?.cleanup()
  }

  handleSignupCheckboxToggle = ({ currentTarget }) => {
    this.submitButton.toggleAttribute('disabled', !currentTarget.checked)
  }

  _handleBlockSelect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.show()
    }
  }

  _handleBlockDeselect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.hide()
    }
  }

  show = async (): Promise<void> => {
    if (this.variant === 'lightbox') {
      const withGradient = !!this.dataset.overlayGradient
      const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

      const background = withGradient
        ? gradientOpacity(
            this.dataset.overlayGradient,
            this.dataset.overlayOpacity,
          )
        : `color-mix(in srgb, ${this.dataset.overlaySolid} ${
            Number(this.dataset.overlayOpacity) * 100
          }%, transparent)`

      const blurFilter = `blur(${this.dataset.overlayBlur}px)`

      whenDefined('body-element').then(() => {
        bodyElement.showOverlay(this.id, {
          zIndex: 8888,
          background,
          backdropFilter: blurFilter,
          '-webkit-backdrop-filter': blurFilter,
          transitionDuration: `${POPUP_ANIMATION_DELAY}ms`,
        })
      })
    }

    this.emit('popupShown', {})

    await super.show()
    this.saveLastShowData()

    const closeBtn = this.selectBtn('close-small')

    if (closeBtn) {
      closeBtn.style.display = 'flex'
    }

    this.addListener(this.selectBtn('close'), 'click', this.hide)
    this.addListener(this.selectBtn('close-small'), 'click', this.hide)
    this.addListener(this.selectBtn('submit'), 'click', this.onSubmit)

    if (this.variant === 'lightbox') {
      this.classList.add(POPUP_CLASSNAMES.lightboxActive)
    }

    if (this.hasImage) {
      if (this.hideImgOnMobile && isMobile()) {
        return
      }

      if (this.isSuccessMessage) {
        return
      }

      setTimeout(() => {
        this.classList.add(POPUP_CLASSNAMES.showImage)
      }, POPUP_ANIMATION_DELAY)
    }
  }

  hide = async (): Promise<void> => {
    this.saveLastShowData()

    if (this.variant === 'lightbox') {
      const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

      whenDefined('body-element').then(() => {
        bodyElement.hideOverlay(this.id)
      })
    }

    this.conditionRuntime?.cleanup()

    this.removeListener(this.selectBtn('close'), 'click', this.hide)
    this.removeListener(this.selectBtn('close-small'), 'click', this.hide)
    this.removeListener(this.selectBtn('submit'), 'click', this.onSubmit)

    if (this.variant === 'lightbox') {
      this.classList.remove(POPUP_CLASSNAMES.lightboxActive)
      this.classList.add(POPUP_CLASSNAMES.lightboxClosing)
    }

    await super.hide()
    this.classList.remove(POPUP_CLASSNAMES.showImage)
    this.classList.remove(POPUP_CLASSNAMES.lightboxClosing)

    this.emit('popupHidden', {})
  }

  onSubmit = (): void => {
    // for success page detection
    window.sessionStorage.setItem(
      'customer-posted-block-id',
      this.dataset.blockId,
    )
  }

  saveLastShowData = () => {
    this.storage.saveData({
      type: PopupType.Signup,
      lastShow: new Date(),
    })
  }

  selectBtn(type: 'submit' | 'close' | 'close-small'): Nullable<HTMLElement> {
    if (!this.isOpen) {
      return null
    }

    return $el(`[data-${type}-btn]`, this)
  }
}
